import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  PropertyKitEntry,
  ASSET_STATUS_LABELS,
  CONSUMABLE_STATUS_LABELS,
  PropertyKitAssetStatus,
  PropertyKitConsumableStatus,
} from '@/hooks/inventory';
import { usePropertyArticles } from '@/hooks/inventory/usePropertyArticles';

interface UpdateEntryModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entry: PropertyKitEntry;
}

export default function UpdateEntryModal({
  open,
  onOpenChange,
  entry,
}: UpdateEntryModalProps) {
  const { updateEntry, isUpdating } = usePropertyArticles(entry.property_id);
  const item = entry.item;
  const controlType = item?.control_type;

  const form = useForm({
    defaultValues: {
      asset_status: entry.asset_status ?? 'present',
      consumable_status: entry.consumable_status ?? 'full',
      current_quantity: entry.current_quantity ?? 0,
      ideal_quantity: entry.ideal_quantity ?? 0,
    },
  });

  useEffect(() => {
    form.reset({
      asset_status: entry.asset_status ?? 'present',
      consumable_status: entry.consumable_status ?? 'full',
      current_quantity: entry.current_quantity ?? 0,
      ideal_quantity: entry.ideal_quantity ?? 0,
    });
  }, [entry, form]);

  const onSubmit = async (data: {
    asset_status: PropertyKitAssetStatus;
    consumable_status: PropertyKitConsumableStatus;
    current_quantity: number;
    ideal_quantity: number;
  }) => {
    try {
      const updateData: {
        entryId: string;
        asset_status?: PropertyKitAssetStatus;
        consumable_status?: PropertyKitConsumableStatus;
        current_quantity?: number;
        ideal_quantity?: number;
      } = { entryId: entry.id };

      switch (controlType) {
        case 'fixed_asset':
          updateData.asset_status = data.asset_status;
          break;
        case 'consumable':
          updateData.consumable_status = data.consumable_status;
          break;
        case 'countable':
          updateData.current_quantity = data.current_quantity;
          updateData.ideal_quantity = data.ideal_quantity;
          break;
      }

      await updateEntry(updateData);
      onOpenChange(false);
    } catch (error) {
      console.error('Error updating entry:', error);
    }
  };

  if (!item) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle>Actualizar "{item.name}"</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            {controlType === 'fixed_asset' && (
              <FormField
                control={form.control}
                name="asset_status"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Estado</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {Object.entries(ASSET_STATUS_LABELS).map(([value, label]) => (
                          <SelectItem key={value} value={value}>
                            {label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormItem>
                )}
              />
            )}

            {controlType === 'consumable' && (
              <FormField
                control={form.control}
                name="consumable_status"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Estado</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {Object.entries(CONSUMABLE_STATUS_LABELS).map(([value, label]) => (
                          <SelectItem key={value} value={value}>
                            {label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormItem>
                )}
              />
            )}

            {controlType === 'countable' && (
              <>
                <FormField
                  control={form.control}
                  name="ideal_quantity"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Cantidad ideal</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min={0}
                          {...field}
                          onChange={(e) => field.onChange(Number(e.target.value))}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="current_quantity"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Cantidad actual</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min={0}
                          {...field}
                          onChange={(e) => field.onChange(Number(e.target.value))}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </>
            )}

            <div className="flex justify-end gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={isUpdating}
                className="bg-brand-primary hover:bg-brand-primary-hover"
              >
                {isUpdating && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Guardar
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
